import {
  CHAMPS_ONCOLOGIE,
  PRECISIONS_DATE,
  STATUTS_TRAITEMENT,
  TYPES_ANTECEDENT,
} from '@doc/shared'
import type { CategorieDonnee } from '../../lib/types'

// Description des formulaires de saisie, catégorie par catégorie. Le formulaire
// générique les lit : il n'y a qu'un endroit à modifier quand un champ change.

export type Champ = {
  cle: string
  etiquette: string
  type: 'texte' | 'zone' | 'date' | 'choix' | 'booleen'
  options?: Record<string, string>
  /** Propositions de saisie, sans rien imposer : la frappe libre reste possible. */
  suggestions?: readonly string[]
  aide?: string
  requis?: boolean
  pleineLargeur?: boolean
}

export const CHAMPS: Partial<Record<CategorieDonnee, Champ[]>> = {
  ANTECEDENT: [
    { cle: 'libelle', etiquette: 'Antécédent', type: 'texte', requis: true, pleineLargeur: true },
    { cle: 'type', etiquette: 'Nature', type: 'choix', options: TYPES_ANTECEDENT },
    { cle: 'cote', etiquette: 'Côté', type: 'texte', aide: 'Droit, gauche, bilatéral' },
    { cle: 'date', etiquette: 'Date', type: 'date' },
    {
      cle: 'precisionDate',
      etiquette: 'Précision de la date',
      type: 'choix',
      options: PRECISIONS_DATE,
      aide: 'Une date approximative reste signalée comme telle.',
    },
    { cle: 'precisions', etiquette: 'Précisions', type: 'zone', pleineLargeur: true },
  ],

  ALLERGIE: [
    { cle: 'substance', etiquette: 'Substance', type: 'texte', requis: true, pleineLargeur: true },
    { cle: 'reaction', etiquette: 'Réaction', type: 'texte' },
    { cle: 'gravite', etiquette: 'Gravité', type: 'texte', aide: 'Légère, modérée, sévère' },
    { cle: 'precisions', etiquette: 'Précisions', type: 'zone', pleineLargeur: true },
  ],

  DIAGNOSTIC: [
    { cle: 'libelle', etiquette: 'Diagnostic', type: 'texte', requis: true, pleineLargeur: true },
    { cle: 'code', etiquette: 'Code', type: 'texte', aide: 'CIM-10 si tu le connais' },
    { cle: 'actif', etiquette: 'Diagnostic actif', type: 'booleen' },
    { cle: 'dateDiagnostic', etiquette: 'Date du diagnostic', type: 'date' },
    {
      cle: 'precisionDate',
      etiquette: 'Précision de la date',
      type: 'choix',
      options: PRECISIONS_DATE,
    },
    { cle: 'precisions', etiquette: 'Précisions', type: 'zone', pleineLargeur: true },
  ],

  TRAITEMENT: [
    {
      cle: 'denomination',
      etiquette: 'Traitement',
      type: 'texte',
      requis: true,
      pleineLargeur: true,
    },
    { cle: 'dci', etiquette: 'Dénomination commune', type: 'texte' },
    { cle: 'dosage', etiquette: 'Dosage', type: 'texte', aide: '500 mg, 1 g…' },
    { cle: 'posologie', etiquette: 'Posologie', type: 'texte', aide: '1 comprimé 3 fois par jour' },
    { cle: 'voie', etiquette: 'Voie', type: 'texte', aide: 'Orale, intraveineuse…' },
    { cle: 'statut', etiquette: 'Statut', type: 'choix', options: STATUTS_TRAITEMENT },
    { cle: 'dateDebut', etiquette: 'Début', type: 'date' },
    {
      cle: 'precisionDate',
      etiquette: 'Précision de la date de début',
      type: 'choix',
      options: PRECISIONS_DATE,
      aide: 'Si le document dit seulement « depuis 2021 », choisis « Année connue ».',
    },
    { cle: 'dateFin', etiquette: 'Fin', type: 'date' },
    { cle: 'indication', etiquette: 'Indication', type: 'texte' },
    { cle: 'prescripteur', etiquette: 'Prescripteur', type: 'texte' },
  ],

  EVENEMENT_HISTOIRE: [
    { cle: 'libelle', etiquette: 'Événement', type: 'texte', requis: true, pleineLargeur: true },
    { cle: 'date', etiquette: 'Date', type: 'date', requis: true },
    {
      cle: 'precisionDate',
      etiquette: 'Précision de la date',
      type: 'choix',
      options: PRECISIONS_DATE,
    },
    { cle: 'categorie', etiquette: 'Catégorie', type: 'texte', aide: 'Libre : symptôme, examen…' },
    {
      cle: 'description',
      etiquette: 'Description',
      type: 'zone',
      pleineLargeur: true,
    },
  ],

  // Les champs des modes obstétrique et oncologie ne sont pas arrêtés : rien
  // n'est imposé, ni stade, ni TNM, ni terme. On enregistre ce qui est saisi.
  DONNEE_SPECIFIQUE: [
    {
      cle: 'domaine',
      etiquette: 'Domaine',
      type: 'choix',
      options: { obstetrique: 'Obstétrique', oncologie: 'Oncologie' },
    },
    {
      cle: 'libelle',
      etiquette: 'Intitulé',
      type: 'texte',
      requis: true,
      suggestions: CHAMPS_ONCOLOGIE,
      aide: 'Histologie, stade, grade… ou ce que tu veux : la liste ne fait que proposer.',
    },
    { cle: 'valeur', etiquette: 'Valeur', type: 'texte', pleineLargeur: true },
    { cle: 'date', etiquette: 'Date', type: 'date' },
  ],
}

export const TITRES_FORMULAIRE: Partial<Record<CategorieDonnee, { ajout: string; edition: string }>> =
  {
    ANTECEDENT: { ajout: 'Ajouter un antécédent', edition: "Corriger l'antécédent" },
    ALLERGIE: { ajout: 'Ajouter une allergie', edition: "Corriger l'allergie" },
    DIAGNOSTIC: { ajout: 'Ajouter un diagnostic', edition: 'Corriger le diagnostic' },
    TRAITEMENT: { ajout: 'Ajouter un traitement', edition: 'Corriger le traitement' },
    EVENEMENT_HISTOIRE: {
      ajout: "Ajouter un événement de l'histoire",
      edition: "Corriger l'événement",
    },
    DONNEE_SPECIFIQUE: { ajout: 'Ajouter une donnée', edition: 'Corriger la donnée' },
  }
